import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import {
  AuthenticatedUser,
  clearStoredImpersonationBackup,
  clearStoredSession,
  impersonateCustomerAccount,
  loginWithPassword,
  readStoredToken,
  readStoredUser,
  restoreAdminSessionFromImpersonation,
  restoreCurrentUser,
  storeSession,
  updateOwnPassword,
} from "../services/auth";

interface AuthContextType {
  user: AuthenticatedUser | null;
  login: (email: string, password: string) => Promise<AuthenticatedUser>;
  changePassword: (password: string) => Promise<AuthenticatedUser>;
  impersonateCustomer: (userId: string) => Promise<AuthenticatedUser>;
  stopImpersonation: () => Promise<AuthenticatedUser>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isImpersonating: boolean;
  isInitializing: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(() => readStoredUser());
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      const token = readStoredToken();

      if (!token) {
        if (!isMounted) {
          return;
        }

        setUser(null);
        clearStoredSession();
        setIsInitializing(false);
        return;
      }

      try {
        const currentUser = await restoreCurrentUser(token);

        if (!isMounted) {
          return;
        }

        if (currentUser) {
          setUser(currentUser);
          storeSession({ token, user: currentUser });
        } else {
          setUser(null);
          clearStoredSession();
        }
      } catch {
        if (isMounted) {
          setUser(null);
          clearStoredSession();
        }
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    restoreSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email: string, password: string): Promise<AuthenticatedUser> => {
    const session = await loginWithPassword(email, password);
    clearStoredImpersonationBackup();
    setUser(session.user);
    storeSession(session);
    return session.user;
  };

  const changePassword = async (password: string): Promise<AuthenticatedUser> => {
    const token = readStoredToken();
    const nextUser = await updateOwnPassword(password, token);
    setUser(nextUser);
    storeSession({ token, user: nextUser });
    return nextUser;
  };

  const impersonateCustomer = async (userId: string): Promise<AuthenticatedUser> => {
    const adminToken = readStoredToken();
    const session = await impersonateCustomerAccount(userId, adminToken);
    setUser(session.user);
    storeSession(session);
    return session.user;
  };

  const stopImpersonation = async (): Promise<AuthenticatedUser> => {
    const session = await restoreAdminSessionFromImpersonation();
    clearStoredImpersonationBackup();
    setUser(session.user);
    storeSession(session);
    return session.user;
  };

  const logout = () => {
    setUser(null);
    clearStoredSession();
    clearStoredImpersonationBackup();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        changePassword,
        impersonateCustomer,
        stopImpersonation,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === "admin",
        isImpersonating: user?.impersonation?.active === true,
        isInitializing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
